// Sign in / create account — used by /login and by the auth modals on the
// landing page, the world builder and the maintenance gate. Same props as the
// old form (onSuccess), so every caller keeps working.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../config/supabase';
import { AlertCircle, ArrowLeft, Check, Eye, EyeOff, Loader, Lock, Mail, MailCheck, RotateCw, User as UserIcon } from 'lucide-react';
import TurnstileWidget, { turnstileEnabled } from './TurnstileWidget';
import { isDisposableEmail } from '../utils/disposableEmail';
import { confirmRedirect, isEmailNotConfirmed, resendConfirmation } from '../services/onboarding';
import './AuthPanel.css';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://mvsjkhswkpefxssnyijs.supabase.co';
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im12c2praHN3a3BlZnhzc255aWpzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTE1MTk4NzMsImV4cCI6MjA2NzA5NTg3M30.hbGc0E9KGmtR59GwujLDivqLaDejGJwYLDkxCNNYkEQ';

/**
 * Is the Google provider switched on in Supabase? Read from the public auth
 * settings rather than a build flag, so the button appears by itself once she
 * enables Google — and never shows as a broken button before that.
 */
let googlePromise: Promise<boolean> | null = null;
export function googleAuthEnabled(): Promise<boolean> {
  if (!googlePromise) {
    googlePromise = fetch(`${SUPABASE_URL}/auth/v1/settings`, { headers: { apikey: SUPABASE_ANON } })
      .then((r) => r.json())
      .then((s) => !!s?.external?.google)
      .catch(() => false);
  }
  return googlePromise;
}

const USERNAME_RE = /^[a-zA-Z0-9_.]{3,20}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Supabase's messages are written for developers; say it the way a person would. */
function friendly(msg: string): string {
  const m = msg.toLowerCase();
  if (m.includes('invalid login credentials')) return "That email and password don't match. Try again or reset your password.";
  if (m.includes('already registered') || m.includes('already been registered')) return 'An account with this email already exists. Sign in instead.';
  if (m.includes('email not confirmed')) return 'Confirm your email first. Tap the link we emailed you, then sign in.';
  if (m.includes('rate limit') || m.includes('too many')) return 'Too many attempts. Wait a minute and try again.';
  if (m.includes('password should be')) return 'Use at least 8 characters for your password.';
  if (m.includes('network') || m.includes('fetch')) return "Can't reach GathrXR right now. Check your connection.";
  return msg;
}

export type AuthMode = 'signin' | 'signup';

interface AuthComponentProps {
  onSuccess?: () => void;
  initialMode?: AuthMode;
  /** Where Google should land after sign-in (a same-site path). */
  next?: string;
  /** Show the heading. Modals that already have one can turn it off. */
  showHeading?: boolean;
}

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const AuthComponent: React.FC<AuthComponentProps> = ({ onSuccess, initialMode = 'signin', next, showHeading = true }) => {
  const [mode, setMode] = useState<AuthMode | 'reset'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [nameState, setNameState] = useState<'idle' | 'checking' | 'free' | 'taken'>('idle');
  const [google, setGoogle] = useState(false);
  // Cloudflare Turnstile: passed to Supabase as captchaToken, which verifies it
  // once Supabase Auth's bot protection is switched on (provider: Turnstile).
  // Without VITE_TURNSTILE_SITE_KEY the widget is off and forms work as before.
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const nameTimer = useRef<number | null>(null);
  // Email confirmation: after sign-up there is no session until the link in
  // the email is tapped. `sentTo` shows the "check your email" state;
  // `unconfirmed` is a sign-in blocked by "Email not confirmed".
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [unconfirmed, setUnconfirmed] = useState<string | null>(null);
  const [resend, setResend] = useState<{ busy: boolean; msg: string | null; ok: boolean }>({ busy: false, msg: null, ok: true });
  const [cooldown, setCooldown] = useState(0);
  const [resendToken, setResendToken] = useState<string | null>(null);
  const [resendReset, setResendReset] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [cooldown]);

  useEffect(() => { void googleAuthEnabled().then(setGoogle); }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) onSuccess?.();
    });
    return () => subscription.unsubscribe();
  }, [onSuccess]);

  // Username availability, debounced. Profiles are publicly readable.
  useEffect(() => {
    if (mode !== 'signup' || !USERNAME_RE.test(username)) { setNameState('idle'); return; }
    setNameState('checking');
    if (nameTimer.current) window.clearTimeout(nameTimer.current);
    nameTimer.current = window.setTimeout(async () => {
      const { data } = await supabase.from('profiles').select('id').ilike('username', username).limit(1);
      setNameState(data && data.length ? 'taken' : 'free');
    }, 350);
    return () => { if (nameTimer.current) window.clearTimeout(nameTimer.current); };
  }, [username, mode]);

  const problems = useMemo(() => {
    const p: Record<string, string> = {};
    if (!EMAIL_RE.test(email.trim())) p.email = 'Enter a valid email address.';
    else if (mode === 'signup' && isDisposableEmail(email)) p.email = 'Please use a permanent email address. Throwaway inboxes can\'t sign up.';
    if (mode === 'signup') {
      if (!USERNAME_RE.test(username)) p.username = '3–20 letters, numbers, _ or .';
      else if (nameState === 'taken') p.username = 'That username is taken.';
      if (password.length < 8) p.password = 'At least 8 characters.';
    } else if (mode === 'signin' && !password) {
      p.password = 'Enter your password.';
    }
    return p;
  }, [email, username, password, mode, nameState]);

  const switchMode = (m: AuthMode | 'reset') => { setMode(m); setError(null); setNotice(null); setTouched({}); setUnconfirmed(null); setSentTo(null); setResend({ busy: false, msg: null, ok: true }); };

  const resendEmail = async (to: string) => {
    if (resend.busy || cooldown > 0) return;
    setResend({ busy: true, msg: null, ok: true });
    const r = await resendConfirmation(to, resendToken, confirmRedirect(next));
    setResend({ busy: false, msg: r.message, ok: r.ok });
    if (r.ok) setCooldown(60); // Supabase sends at most one per address per minute
    if (turnstileEnabled) { setResendToken(null); setResendReset((n) => n + 1); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ email: true, username: true, password: true });
    setError(null);
    setNotice(null);
    setUnconfirmed(null);
    const relevant = mode === 'reset' ? ['email'] : Object.keys(problems);
    if (relevant.some((k) => problems[k])) return;
    if (turnstileEnabled && !captcha) { setError('Please complete the "I\'m human" check.'); return; }
    const captchaToken = captcha || undefined;
    setLoading(true);
    try {
      if (mode === 'signup') {
        const { data, error } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: { data: { username: username.trim() }, captchaToken, emailRedirectTo: confirmRedirect(next) },
        });
        if (error) throw error;
        // With confirmation on, there is no session yet. An address that is
        // already registered comes back as a user with no identities (and no
        // email is sent), so point those people at Sign in instead.
        if (data.user && !data.session) {
          if (Array.isArray(data.user.identities) && data.user.identities.length === 0) {
            setError('An account with this email already exists. Sign in instead.');
          } else {
            setSentTo(email.trim());
            setResend({ busy: false, msg: null, ok: true });
            setCooldown(60);
          }
        }
      } else if (mode === 'signin') {
        const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password, options: { captchaToken } });
        if (error && isEmailNotConfirmed(error)) { setUnconfirmed(email.trim()); setResend({ busy: false, msg: null, ok: true }); return; }
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
          redirectTo: `${window.location.origin}/login?reset=1`,
          captchaToken,
        });
        if (error) throw error;
        setNotice('If that email has an account, a reset link is on its way.');
      }
    } catch (err: any) {
      setError(friendly(err?.message || 'Something went wrong.'));
    } finally {
      setLoading(false);
      if (turnstileEnabled) setCaptchaReset((n) => n + 1); // tokens are single-use
    }
  };

  const handleGoogle = async () => {
    setError(null);
    const dest = next && next.startsWith('/') ? next : '/dashboard';
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/welcome?next=${encodeURIComponent(dest)}` },
    });
    if (error) setError(friendly(error.message));
  };

  const fieldErr = (k: string) => (touched[k] && problems[k] ? problems[k] : null);

  const resendControls = (to: string) => (
    <>
      <TurnstileWidget className="gxa-turnstile" action="resend" onToken={setResendToken} resetKey={resendReset} />
      {resend.msg && <div className={`gxa-alert ${resend.ok ? 'is-ok' : ''}`} role="status">{resend.ok ? <Check size={16} /> : <AlertCircle size={16} />} {resend.msg}</div>}
      <button
        type="button" className="gxa-secondary" onClick={() => void resendEmail(to)}
        disabled={resend.busy || cooldown > 0 || (turnstileEnabled && !resendToken)}
      >
        {resend.busy ? <Loader size={15} className="spin" /> : <RotateCw size={15} />}
        {cooldown > 0 ? `Resend email in ${cooldown}s` : 'Resend email'}
      </button>
    </>
  );

  // Signed up, waiting for the confirmation link.
  if (sentTo) {
    return (
      <div className="gxa">
        <div className="gxa-sent">
          <span className="gxa-sent-icon" aria-hidden><MailCheck size={26} /></span>
          <h2>Check your email</h2>
          <p>We sent a link to <strong>{sentTo}</strong>. Tap it to confirm your account and pick your free robot avatar.</p>
          <p className="gxa-sent-tip">Can't find it? It can take a minute. Check your spam or promotions folder for mail from hello@gathrxr.com.</p>
        </div>
        <div className="gxa-form">
          {resendControls(sentTo)}
        </div>
        <button type="button" className="gxa-back" onClick={() => { setSentTo(null); switchMode('signin'); }}>
          Already confirmed? Sign in
        </button>
        <button type="button" className="gxa-back gxa-back-tight" onClick={() => { setSentTo(null); setMode('signup'); setResend({ busy: false, msg: null, ok: true }); }}>
          <ArrowLeft size={14} /> Use a different email
        </button>
      </div>
    );
  }

  return (
    <div className="gxa">
      {showHeading && (
        <div className="gxa-head">
          <h2>{mode === 'signup' ? 'Create your account' : mode === 'reset' ? 'Reset your password' : 'Welcome back'}</h2>
          <p>
            {mode === 'signup' ? 'Build worlds, make your avatar and hang out together.'
              : mode === 'reset' ? "Enter your email and we'll send you a link."
              : 'Sign in to your worlds, friends and avatar.'}
          </p>
        </div>
      )}

      {mode !== 'reset' && (
        <div className="gxa-tabs" role="tablist">
          <button role="tab" aria-selected={mode === 'signin'} className={mode === 'signin' ? 'is-on' : ''} onClick={() => switchMode('signin')} type="button">Sign in</button>
          <button role="tab" aria-selected={mode === 'signup'} className={mode === 'signup' ? 'is-on' : ''} onClick={() => switchMode('signup')} type="button">Create account</button>
          <span className="gxa-tabs-ink" data-mode={mode} aria-hidden />
        </div>
      )}

      {google && mode !== 'reset' && (
        <>
          <button type="button" className="gxa-google" onClick={handleGoogle}>
            <GoogleMark /> {mode === 'signup' ? 'Sign up with Google' : 'Continue with Google'}
          </button>
          <div className="gxa-or"><span>or with email</span></div>
        </>
      )}

      <form onSubmit={handleSubmit} className="gxa-form" noValidate>
        {mode === 'signup' && (
          <label className={`gxa-field ${fieldErr('username') ? 'has-err' : ''}`}>
            <span className="gxa-label">Username</span>
            <span className="gxa-input">
              <UserIcon size={17} aria-hidden />
              <input
                type="text" autoComplete="username" placeholder="yourname" value={username}
                onChange={(e) => setUsername(e.target.value.replace(/\s/g, ''))}
                onBlur={() => setTouched((t) => ({ ...t, username: true }))}
                maxLength={20}
              />
              {nameState === 'checking' && <Loader size={15} className="spin gxa-adorn" />}
              {nameState === 'free' && <Check size={15} className="gxa-adorn is-ok" aria-label="Available" />}
            </span>
            {fieldErr('username') && <span className="gxa-hint">{fieldErr('username')}</span>}
          </label>
        )}

        <label className={`gxa-field ${fieldErr('email') ? 'has-err' : ''}`}>
          <span className="gxa-label">Email</span>
          <span className="gxa-input">
            <Mail size={17} aria-hidden />
            <input
              type="email" autoComplete="email" inputMode="email" placeholder="you@example.com" value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            />
          </span>
          {fieldErr('email') && <span className="gxa-hint">{fieldErr('email')}</span>}
        </label>

        {mode !== 'reset' && (
          <label className={`gxa-field ${fieldErr('password') ? 'has-err' : ''}`}>
            <span className="gxa-label">
              Password
              {mode === 'signin' && <button type="button" className="gxa-link" onClick={() => switchMode('reset')}>Forgot?</button>}
            </span>
            <span className="gxa-input">
              <Lock size={17} aria-hidden />
              <input
                type={showPw ? 'text' : 'password'}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                placeholder={mode === 'signup' ? 'At least 8 characters' : 'Your password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, password: true }))}
              />
              <button type="button" className="gxa-eye" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? 'Hide password' : 'Show password'}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </span>
            {mode === 'signup' && password && (
              <span className="gxa-meter" data-level={password.length >= 12 && /\d/.test(password) && /[^a-z0-9]/i.test(password) ? 3 : password.length >= 8 ? 2 : 1} aria-hidden><i /><i /><i /></span>
            )}
            {fieldErr('password') && <span className="gxa-hint">{fieldErr('password')}</span>}
          </label>
        )}

        <TurnstileWidget className="gxa-turnstile" action={mode} onToken={setCaptcha} resetKey={captchaReset} />

        {error && <div className="gxa-alert" role="alert"><AlertCircle size={16} /> {error}</div>}
        {unconfirmed && mode === 'signin' && (
          <div className="gxa-unconfirmed" role="alert">
            <div className="gxa-alert is-warn"><Mail size={16} /> <span>Please confirm your email first. We sent a link to <strong>{unconfirmed}</strong> when you signed up. Tap it, then sign in. Check your spam folder too.</span></div>
            {resendControls(unconfirmed)}
          </div>
        )}
        {notice && <div className="gxa-alert is-ok" role="status"><Check size={16} /> {notice}</div>}

        <button type="submit" className="gxa-submit" disabled={loading}>
          {loading ? <Loader size={17} className="spin" /> : null}
          {mode === 'signup' ? 'Create account' : mode === 'reset' ? 'Send reset link' : 'Sign in'}
        </button>
      </form>

      {mode === 'reset' ? (
        <button type="button" className="gxa-back" onClick={() => switchMode('signin')}><ArrowLeft size={14} /> Back to sign in</button>
      ) : mode === 'signup' ? (
        <p className="gxa-fine">
          By creating an account you agree to our{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>, and to be excellent to everyone you meet in GathrXR.
        </p>
      ) : null}
    </div>
  );
};

export default AuthComponent;
